// Basic type definitions
export type TimeUnit =
	| 'weeks'
	| 'days'
	| 'hours'
	| 'minutes'
	| 'seconds'
	| 'milliseconds'
	| 'microseconds';

// Common types
type BaseVariable = {
	variableName: string;
	variableLabel: string;
	obligatory: boolean;
	description: string;
	entryType?: 'entry' | 'calculated';
};

type FixedCategoryBase = {
	fixedCategories: 'yes';
	allowedCategories: CategoryOption[];
	optimizeForManyValues: boolean;
};

type NonFixedCategoryBase = {
	fixedCategories: 'no';
	categories: string[];
	optimizeForManyValues: boolean;
};

export type CategoryOption = {
	description: string;
	label: string;
	value: string;
	id: string;
};

type ValidationEnabled = {
	validationCases?: ValidationCase[];
};

// Specific variable types
export type CategoryNonFixedVariable = BaseVariable &
	NonFixedCategoryBase & {
		variableType: 'category';
	};
export const isCategoryFixedVariable = (variable: Variable): variable is CategoryFixedVariable =>
	variable.variableType === 'category' && variable.fixedCategories === 'yes';

export type CategoryFixedVariable = BaseVariable &
	FixedCategoryBase & {
		variableType: 'category';
	};
export const isCategoryNonFixedVariable = (
	variable: Variable
): variable is CategoryNonFixedVariable =>
	variable.variableType === 'category' && variable.fixedCategories === 'no';

export type CategoryVariable = CategoryNonFixedVariable | CategoryFixedVariable;
export const isCategoryVariable = (variable: Variable): variable is CategoryVariable =>
	variable.variableType === 'category';

export type CategoryMultipleNonFixedVariable = BaseVariable &
	NonFixedCategoryBase & {
		variableType: 'categoryMultiple';
	};
export const isCategoryMultipleNonFixedVariable = (
	variable: Variable
): variable is CategoryMultipleNonFixedVariable =>
	variable.variableType === 'categoryMultiple' && variable.fixedCategories === 'no';

export type CategoryMultipleFixedVariable = BaseVariable &
	FixedCategoryBase & {
		variableType: 'categoryMultiple';
	};
export const isCategoryMultipleFixedVariable = (
	variable: Variable
): variable is CategoryMultipleFixedVariable =>
	variable.variableType === 'categoryMultiple' && variable.fixedCategories === 'yes';

export type CategoryMultipleVariable =
	| CategoryMultipleNonFixedVariable
	| CategoryMultipleFixedVariable;

export const isCategoryMultipleVariable = (
	variable: Variable
): variable is CategoryMultipleVariable => variable.variableType === 'categoryMultiple';

export type StringVariable = BaseVariable & {
	variableType: 'string';
};

export type IntegerVariable = BaseVariable &
	ValidationEnabled & {
		variableType: 'integer';
	};

export type FloatVariable = BaseVariable &
	ValidationEnabled & {
		variableType: 'float';
	};

export type DateVariable = BaseVariable &
	ValidationEnabled & {
		variableType: 'date';
	};

export type DateTimeVariable = BaseVariable &
	ValidationEnabled & {
		variableType: 'datetime';
	};

export type FileVariable = BaseVariable & {
	variableType: 'file';
};

export type UserDefinedUniqueVariable = BaseVariable & {
	variableType: 'userDefinedUnique';
	uniquenessType: 'Manual' | 'Sequence' | 'UUID';
};

export type TimeDurationVariable = BaseVariable & {
	variableType: 'timeDuration';
	durationFormat: {
		maxTimeUnit: TimeUnit;
		minTimeUnit: TimeUnit;
	};
};

export type VariableGroup = {
	groupLabel: string;
	groupName: string;
	variablesBelongingToGroup: string[];
};

type GroupOrder = {
	group: string;
	variable?: never;
	set?: never;
};
export type VariableOrGroupOrder = { variable: string; group?: never; set?: never } | GroupOrder;

export const isGroupVariableOrder = (order: VariableOrGroupOrder): order is GroupOrder => {
	return 'group' in order;
};

export type Order = VariableOrGroupOrder | { variable?: never; group?: never; set: string };

export type VariableSeries = {
	setLabel: string;
	setName: string;
	setOrder: VariableOrGroupOrder[];

	aggregationRules?: AggregationRule[];
	enableSurvey?: boolean;
};
export type AggregationRule = {
	aggregator: {
		variableName: string;
	};
	name: string;
	rule: {
		type: AggregationRuleType;
	};
};

export type AggregationRuleType =
	| 'average'
	| 'sum'
	| 'median'
	| 'min'
	| 'max'
	| 'earliest'
	| 'latest';

export type VariableGroups = Record<string, VariableGroup[]>;

export type SystemVariable = {
	variableType: 'system';
	variableName:
		| 'enteredbyuser'
		| 'ownedbyuser'
		| 'creationdate'
		| 'lastmodifieddate'
		| 'datasetentryid';
	variableLabel: string;
};
export const isSystemVariable = (
	variable?: Variable | SystemVariable
): variable is SystemVariable =>
	variable !== undefined && 'variableType' in variable && variable.variableType === 'system';

// Union type for all variables
export type Variable =
	| CategoryNonFixedVariable
	| CategoryFixedVariable
	| CategoryMultipleNonFixedVariable
	| CategoryMultipleFixedVariable
	| StringVariable
	| IntegerVariable
	| FloatVariable
	| DateVariable
	| DateTimeVariable
	| FileVariable
	| UserDefinedUniqueVariable
	| TimeDurationVariable;

export type VariableType = Variable['variableType'];

// Validation types
export type ValidationCase = {
	validationExpression: ValidationExpression;
};

export type ValidationExpression = {
	operator: Operator;
	operands: Operand[];
};

export type Operator =
	| 'op1_or_op2'
	| 'op1_float_val'
	| 'op1_integer_val'
	| 'op1_date_val'
	| 'op1_greater_than_or_equals_op2'
	| 'op1_less_than_or_equals_op2'
	| 'op1_variable_ref'
	| 'no_op_null_val'
	| 'op1_equals_op2';

export type Operand = ValidationExpression | string | number | boolean;

export type StatusDefinition = {
	default: boolean;
	description: string;
	dueTimeAmount: number;
	dueTimeUnit: StatusDueTimeUnit;
	statusColor: string;
	statusLabel: string;
	statusName: string;
};

export type StatusDueTimeUnit = 'hours' | 'days' | 'weeks';

export type EntryStatus = {
	comment?: string;
	dueTimeStamp: string;
	value: boolean;
};

export type BackendEntryValue = string | number | null | string[];
export const isEmptyBackendEntryValue = (value: BackendEntryValue) => {
	if (Array.isArray(value)) {
		return value.length === 0;
	}

	return value === null || value === undefined;
};
export type Entry = {
	[key: string]: any;
};

/**
 * Including these types on the base Entry caused a lot of type errors inside of the EntryForm, so they need to be added explicitly when needed.
 */
export type OurInternalCustomEntryFields = {
	active: boolean;
	creationdate: string;
	datasetentryid: string;
	enteredbyuser: string;
	lastmodifieddate: string;
	original_id: string;
	ownedbyuser: string;
	parentsetrevisionid: string;
	userProjectOrgId: number;
};

export type EntryAccess = {
	delete: boolean;
	write: boolean;
};

export type DependencyOperator = '<' | '<=' | '=' | '>=' | '>';

export type VisibilityDependencyRule = {
	dependencyType: 'Visibility';
	supplierVariable: Variable;
	dependantVariable: Variable;
	operator: DependencyOperator;
	value: string;
};

export type FilteringDependencyRule = {
	dependencyType: 'Filtering';
	supplierVariable: Variable;
	dependantVariable: Variable;
	operator: DependencyOperator;
	value: string;
	filteredValues: string[];
};
export const isFilteringDependencyRule = (rule: DependencyRule): rule is FilteringDependencyRule =>
	rule.dependencyType === 'Filtering';
export const isVisibilityDependencyRule = (
	rule: DependencyRule
): rule is VisibilityDependencyRule => rule.dependencyType === 'Visibility';

export type DependencyRule = VisibilityDependencyRule | FilteringDependencyRule;
